<template>
    <main class="marketing">
        <ComponentsTemplate
            :components="separatedComponents.relativeComponents"
            :watermark="watermark"
            :series="series"
            :pageConfig="pageConfig"
            :style="relativeComponentsWrapStyle"
            :lpConfig = "lpConfig"
            rootMark="relativeComponentsRef"
            ref="relativeComponentsRef"
            class="relative-components-wrap"
            :trackExposeAction="trackExposeAction"
            :trackSelectAction="trackSelectAction"
            :trackCommonAction="trackCommonAction"
            :trackClickLink="trackClickLink"
            @inputFocus="handleInputFocus"
            @inputBlur="handleInputBlur"
        />
        <ComponentsTemplate
            :components="separatedComponents.fixedComponents"
            :watermark="watermark"
            :series="series"
            :pageConfig="pageConfig"
            :lpConfig = "lpConfig"
            rootMark="fixedComponentsRef"
            ref="fixedComponentsRef"
            class="fixed-components-wrap"
            :trackExposeAction="trackExposeAction"
            :trackSelectAction="trackSelectAction"
            :trackCommonAction="trackCommonAction"
            :trackClickLink="trackClickLink"
            @inputFocus="handleInputFocus"
            @inputBlur="handleInputBlur"
        />
        <watermask v-if="watermarkText" :text="watermarkText" />
        <succeed
            :is-show="successStatus !== 0"
            :succeedType="successStatus"
            :carType="pageConfig.presetModel"
        ></succeed>
        <coming-soon v-if="comingSoon" :text="comingSoonText" />
        <network-error v-if="networkError" />
    </main>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex';
import wxSDK from '@/util/third-party/wx-js-sdk.js';
import { isInNioCNApp, getLoginInfoBridge } from '@/util/platform/universal';
import ComponentsTemplate from './components/components-template';
import Watermask from './components/watermask';
import comingSoon from '@/marketing_components/coming-soon'; // @/components/coming-soon
import networkError from '@/marketing_components/network-error'; // @/components/network-error
import {
    PAGE_MODE_MAP,
    NEW_CAR_SERIES_LIST,
    DMP_COMPONENT_IS_CLICKABLE
} from '../../config/enums';
import { LOGIN_INFO_EVENT_NAME } from './config';
import { getDomVisibleSettingCompatible } from '../../util/dom';
import { isAndroid } from '@/util/browser.js';
import { separateComponents } from '@/util/dmp/index';
import { configShare, hideMenuItems } from '@/util/platform';
const watermarkTemplate = {
    draft: '草稿',
    preview: '预览'
};

const mixin = {
    methods: {
        ...mapActions('marketing/', [
            'init',
            'getWxConfigSig',
            'getAccount',
            'campaignOptionInit',
            'getActivityByCouponIssueId',
            'getGisAreaList'
        ]),
        ...mapMutations('marketing/', [
            'setComingSoon',
            'setCampaignOption',
            'setLoginInfo'
        ])
    },
    computed: {
        ...mapState(['configCarV2Data']),
        ...mapState('marketing/', [
            'watermark',
            'campaignOption',
            'pageConfig',
            'trackComponentNames',
            'successStatus',
            'comingSoon',
            'networkError',
            'getAccountData'
        ])
    }
};

export default {
    mixins: [mixin],
    data() {
        return {
            scrollTop: 0,
            oldScrollTop: 0,
            lastTrackScrollY: 0,
            isScrolling: false,
            isTapping: false,
            exposedComponents: [],
            relativeComponentsWrapStyle: '',
            fixedElementObserver: null,
            lpConfig: {
                lpId: null,
                lpToken: null
            },
            gisProvinces$: this.loadGisProvinces(),
            gisProvinces: []
        };
    },
    // 渲染前调用
    asyncData({ store, route, host }) {
        const { dispatch, state } = store;
        return dispatch('marketing/init', {
            ...route,
            host
        }).then(res => ({
            ...res,
            title: state.marketing.pageConfig.title || 'NIO 蔚来'
        }));
    },
    async mounted() {
        try {
            // 解析uniqueKey
            const param = this.$route;
            await this.campaignOptionInit({
                ...param.query,
                ...param.params
            });
            if (
                this.campaignOption.expire_time &&
                new Date().getTime() > this.campaignOption.expire_time
            ) {
                this.$message('二维码已过期');
                return;
            }
            if (this.campaignOption.couponIssueId) {
                await this.getCampaignByCouponIssueId(
                    this.campaignOption.couponIssueId
                );
            }
        } catch (error) {
            console.log(error, '解析uniqueKey报错');
        }
        this.validateCarType();

        // lpid/token处理
        const [lp_id, lp_token] = this.pageConfig.lpid.split('_');
        this.lpConfig.lpId = lp_id;
        this.lpConfig.lpToken = lp_token;

        if (this.pageConfig.presetAreaCode) {
            try {
                const city = await this.getCityByAreaCode(
                    this.pageConfig.presetAreaCode
                );
                if (city) {
                    this.pageConfig.presetAreaName = city.city_name;
                }
            } catch (e) {
                console.log(e);
            }
        }
        if (this.campaignOption.accountId) {
            this.getAccount({
                account_id: this.campaignOption.accountId
            });
        }
        this.trackAction('enter', {
            lp_component: this.trackComponentNames,
            tenant_code: this.corpAuthenticationEnterpriseCode
        });
        this.initThirdParty();
        this.initPageVisibleListener();
        this.handleHash();
        this.nioAppPageInit();
        this.initFixedComponentsObserver();
    },
    beforeDestroy() {
        this.removeFixedComponentsObserver();
    },
    methods: {
        async loadGisProvinces() {
            return this.getGisAreaList().then(({ data }) => {
                this.gisProvinces = (data.provinces || []).map(x => ({
                    id: +x.areaCode,
                    name: x.name,
                    cities: (x.cities || []).map(y => ({
                        city_id: +y.areaCode,
                        city_name: y.name
                    }))
                }));
            });
        },
        // 第三方初始化
        async initThirdParty() {
            try {
                const { shareParams = {} } = this.pageConfig;
                await configShare(shareParams);
                if (!shareParams || !shareParams.title) {
                    await hideMenuItems();
                }
            } catch (e) {
                console.log('init wx failed:', e);
            }
        },
        /**
         * app内 初始化
         */
        async nioAppPageInit() {
            if (!isInNioCNApp()) return;
            const { data } = await getLoginInfoBridge();
            this.setLoginInfo(data);
            this.$root.$emit(LOGIN_INFO_EVENT_NAME, data);
        },
        async getCityByAreaCode(areaCode) {
            const province_id = areaCode.replace(/[0-9]{4}$/, '0000');

            await this.gisProvinces$;
            const selectedProvince = this.gisProvinces.find(
                x => String(x.id) === String(province_id)
            );

            const selectedCities =
                selectedProvince && selectedProvince.cities
                    ? selectedProvince.cities
                    : [];

            return selectedCities.find(
                item => String(item.city_id) === String(areaCode)
            );
        },
        initPageVisibleListener() {
            const {
                hidden,
                visibilityChange,
                state
            } = getDomVisibleSettingCompatible();

            if (!hidden || !state || !visibilityChange) {
                console.warn('No DOM visible setting');
                return;
            }
            document.addEventListener(
                visibilityChange,
                () => {
                    if (document[state] !== hidden) {
                        this.updateTaskComps();
                    }
                },
                false
            );
        },
        updateTaskComps() {
            const { relativeComponentsRef, fixedComponentsRef } =
                this.$refs || {};

            relativeComponentsRef &&
                relativeComponentsRef.updateTaskComps &&
                relativeComponentsRef.updateTaskComps();

            fixedComponentsRef &&
                fixedComponentsRef.updateTaskComps &&
                fixedComponentsRef.updateTaskComps();
        },
        async trackAction(action, custom = {}) {
            if (!this.watermark) {
                const carConfig = this.configCarV2Data[
                    this.pageConfig.presetModel.toLowerCase()
                ];
                const realCarType =
                    (carConfig && carConfig.code) ||
                    this.pageConfig.presetModel;
                const content = {
                    step_name: action,
                    lp_id: this.lpConfig.lpId,
                    campaign_code: this.pageConfig.campaignSourceCode,
                    invite_account: this.campaignOption.accountId,
                    car_model: realCarType.toUpperCase(),
                    city: this.pageConfig.presetAreaName,
                    page: PAGE_MODE_MAP[this.pageConfig.mode],
                    ...custom,
                    // 新版埋点增加
                    requirement_id: 1435,
                    track_sign: 'aCar.b1013.c1799.d1809.result.RetentionFlow',
                    link: window.location.href,
                    fellow_emp_domain: this.campaignOption.fellowEmpDomain,
                    fellow_name: this.campaignOption.registerEmpDomain
                };
                await this.$tracker.collectPlus('RetentionFlow', content);
            }
        },
        async trackSelectAction(item = { props: {} }, custom = {}) {
            const content = {
                component_id: item.props.componentId,
                mkt_type: item.props.trackName,
                position_rank: item.props.positionRank,
                is_clickable: DMP_COMPONENT_IS_CLICKABLE[item.type] || false,
                ...custom
            };
            return await this.trackAction('select_component', content);
        },
        trackExposeAction(item = { props: {} }, custom = {}) {
            if (this.watermark || !item.props) return;
            const content = {
                requirement_id: 914,
                track_sign: 'aCar.b1013.c1799.d1800.exposure.RetentionFlowExpo',
                lp_id: this.lpConfig.lpId,
                campaign_code: this.pageConfig.campaignSourceCode,
                link: window.location.href,
                page: PAGE_MODE_MAP[this.pageConfig.mode],
                // 组件自己的属性
                component_id: item.props.componentId,
                mkt_type: item.props.trackName,
                position_rank: item.props.positionRank,
                fellow_emp_domain: this.campaignOption.fellowEmpDomain,
                ...custom
            };
            this.$tracker.collectPlus('RetentionFlowExpo', content);
        },
        async trackClickLink(item = { props: {} }, custom = {}) {
            return await this.trackAction('guide_click', {
                component_id: item.props.componentId,
                mkt_type: item.props.trackName,
                position_rank: item.props.positionRank,
                is_clickable: true,
                ...custom
            });
        },
        async trackCommonAction(item = { props: {} }, step, custom = {}) {
            return await this.trackAction(step, {
                component_id: item.props.componentId,
                mkt_type: item.props.trackName,
                position_rank: item.props.positionRank,
                is_clickable: true,
                ...custom
            });
        },
        handleHash() {
            if (!location.hash) return;
            try {
                const hash = JSON.parse(
                    decodeURIComponent(location.hash.slice(1))
                );
                if (!hash.jump === 'immediate') return;

                const wrap = this.$refs[hash.from] || {};
                const {
                    marketingLink = [],
                    marketingImage = [],
                    marketingAward = [],
                    marketingLeadsAward = []
                } =
                    wrap.$refs || {};
                const triggerComponent = [
                    ...marketingLink,
                    ...marketingImage,
                    ...marketingAward,
                    ...marketingLeadsAward
                ].find(i => i.index === hash.index);

                if (triggerComponent) {
                    triggerComponent.handleClick('toHash', hash.target);
                }
            } catch (error) {
                console.log(error);
            }
        },
        validateCarType() {
            // 车型校验
            if (this.pageConfig.presetModel) {
                const allowCarTypes = Object.keys(this.configCarV2Data);
                const comingSoon =
                    NEW_CAR_SERIES_LIST.includes(
                        this.pageConfig.presetModel.toLowerCase()
                    ) &&
                    allowCarTypes.indexOf(
                        this.pageConfig.presetModel.toLowerCase()
                    ) < 0;
                this.setComingSoon(comingSoon);
            }
        },
        async getCampaignByCouponIssueId(id) {
            if (!id) return;
            try {
                const res = await this.getActivityByCouponIssueId({
                    coupon_issue_id: id
                });
                if (
                    res.result_code === 'success' &&
                    res.data &&
                    res.data.campaign_code
                ) {
                    this.setCampaignOption({
                        campaignCode: res.data.campaign_code,
                        campaign_code: res.data.campaign_code
                    });
                }
            } catch (e) {
                console.log('get coupon campaign error: ', e);
            }
        },
        initFixedComponentsObserver() {
            const fixedEl = document.querySelector('.fixed-components-wrap');
            const config = { childList: true, subtree: true };
            const callback = () => {
                this.relativeComponentsWrapStyle = {
                    height: `calc(100vh - ${fixedEl.offsetHeight}px)`, // 用于expose的判定
                    maxHeight: `calc(100dvh - ${fixedEl.offsetHeight}px)`, // 动态窗口高度 兼容safari等导航导致的双滚动条问题，vh >= dvh，但dvh兼容性不太行所以用maxHeight先限制一下
                    marginBottom: `${fixedEl.offsetHeight}px` // 垫高
                };
            };

            if (!this.fixedElementObserver && MutationObserver) {
                this.fixedElementObserver = new MutationObserver(callback);
                this.fixedElementObserver.observe(fixedEl, config);
            }

            // 初始化
            this.relativeComponentsWrapStyle = {
                height: `calc(100vh - ${fixedEl.offsetHeight}px)`, // 用于expose的判定
                maxHeight: `calc(100dvh - ${fixedEl.offsetHeight}px)`, // 动态窗口高度 兼容safari等导航导致的双滚动条问题，vh >= dvh，但dvh兼容性不太行所以用maxHeight先限制一下
                marginBottom: `${fixedEl.offsetHeight}px` // 垫高
            };
        },
        removeFixedComponentsObserver() {
            if (this.fixedElementObserver) {
                this.fixedElementObserver.disconnect();
                this.fixedElementObserver = null;
            }
        },
        handleInputFocus(rootMark, targetElement) {
            const target = this.$refs[rootMark] || {};
            const el = target.$el || {};
            const scrollHeight = el.scrollHeight;
            const screenHeight = window.screen.height * 2;

            if (isAndroid() && scrollHeight < screenHeight) {
                setTimeout(() => {
                    const elementnode = document.createElement('div');
                    elementnode.setAttribute('id', 'android_bottom_dev');
                    elementnode.setAttribute(
                        'style',
                        'width:100%; height:40vh;'
                    );
                    el.appendChild(elementnode);

                    targetElement && targetElement.scrollIntoViewIfNeeded();
                }, 400);
            }
        },
        handleInputBlur() {
            const dom = document.getElementById('android_bottom_dev');
            if (isAndroid() && dom) {
                dom.remove();
                // elementnode.parentNode.removeChild(elementnode);
            }
        }
    },
    computed: {
        series() {
            const allowCarTypes = Object.keys(this.configCarV2Data).filter(
                k => !this.configCarV2Data[k].grayRelease
            );
            return allowCarTypes.map(type => {
                const realType =
                    (this.configCarV2Data[type] &&
                        this.configCarV2Data[type].carType) ||
                    type;
                const label =
                    (this.configCarV2Data[type] &&
                        this.configCarV2Data[type].label) ||
                    `NIO ${realType.toUpperCase()}`;
                const codeName =
                    (this.configCarV2Data[type] &&
                        this.configCarV2Data[type].name) ||
                    realType.toUpperCase();
                return {
                    label,
                    value: type,
                    code: realType,
                    codeName,
                    avatar: this.configCarV2Data[type].seriesImg
                };
            });
        },
        watermarkText() {
            return watermarkTemplate[this.watermark];
        },
        comingSoonText() {
            if (this.pageConfig.mode === 'drive') {
                return '敬请期待 蔚来新车试驾';
            } else if (this.pageConfig.mode === 'leads') {
                return '敬请期待 蔚来新车发布';
            } else {
                return '';
            }
        },
        separatedComponents() {
            return separateComponents(this.pageConfig.components);
        },
        corpAuthenticationEnterpriseCode() {
            const { components = [] } = this.pageConfig;
            const corpAuthenticationComp = components.find(
                item => item.type === 'corp_authentication'
            );

            return corpAuthenticationComp
                ? corpAuthenticationComp.props.enterpriseCode
                : '';
        }
    },
    components: {
        ComponentsTemplate,
        Watermask,
        comingSoon,
        networkError,
        succeed: () => import('@/marketing_components/succeed/index') // @/components/succeed/index
    }
};
</script>

<style lang="less" scoped>
@import '~src/assets/css/mixin.less';
@import '~src/assets/css/variable.less';

.marketing {
    position: relative;
    overflow: hidden;
    background: #fff;
    height: 100vh;
    height: 100dvh;

    .relative-components-wrap {
        overflow-y: scroll;
    }

    .fixed-components-wrap {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        z-index: 99;
        height: fit-content;
    }
}
</style>



// WEBPACK FOOTER //
// src/views/marketing/index.vue